import { useEffect, useLayoutEffect, useRef } from 'react';

// Mirrors NvDropdown's own navigation selector.
// '[tabindex="0"]' (without the bs4-dropdown-item guard) is intentional: accordion
// headers render as ClickableContainer with tabIndex=0 but no bs4-dropdown-item class,
// so the narrower '.bs4-dropdown-item[tabindex="0"]' would miss them.
const FOCUSABLE_ITEMS_SELECTOR = 'a.bs4-dropdown-item, [tabindex="0"]';

/**
 * Restores keyboard focus to the previously focused dropdown item when an async
 * Redux-triggered re-render removes/recreates the item node while the menu is open.
 *
 * Tracking works by listening for `focusin` events inside the `.bs4-dropdown-menu`
 * contained within `containerRef`. After every render, if the tracked element is no
 * longer in the DOM and focus hasn't moved somewhere meaningful outside the menu,
 * focus is restored to the same index (clamped if the list shrank).
 *
 * @param containerRef - ref to the wrapper element that contains the `.bs4-dropdown-menu`
 * @param isOpen       - whether the dropdown is currently open
 */
const useDropdownFocusRestore = (
  containerRef: React.RefObject<HTMLElement>,
  isOpen: boolean,
): void => {
  const focusedItemRef = useRef<{ index: number; element: HTMLElement } | null>(null);

  // While the dropdown is open, track which item last received focus.
  useEffect(() => {
    if (!isOpen) {
      focusedItemRef.current = null;
      return undefined;
    }

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      const dropdownMenu = containerRef.current?.querySelector<HTMLElement>('.bs4-dropdown-menu');
      if (!dropdownMenu?.contains(target)) return;

      const focusableItems = Array.from(
        dropdownMenu.querySelectorAll<HTMLElement>(FOCUSABLE_ITEMS_SELECTOR),
      );

      // Direct hit first; fall back to closest ancestor that matches the selector
      // in case an inner child (e.g. an icon or label) received the event instead.
      let index = focusableItems.indexOf(target);
      let effectiveTarget = target;
      if (index === -1) {
        const ancestor = target.closest<HTMLElement>(FOCUSABLE_ITEMS_SELECTOR);
        if (ancestor) {
          index = focusableItems.indexOf(ancestor);
          effectiveTarget = ancestor;
        }
      }

      if (index >= 0) {
        focusedItemRef.current = { index, element: effectiveTarget };
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [isOpen, containerRef]);

  // After every render, if the previously focused item was removed from the DOM by an
  // async re-render, restore focus to the same index position in the updated item list.
  useLayoutEffect(() => {
    if (!isOpen || !focusedItemRef.current) return;

    const { index: savedIndex, element: savedElement } = focusedItemRef.current;

    // Element still in the DOM — user moved focus away intentionally, don't trap them.
    if (document.contains(savedElement)) return;

    const dropdownMenu = containerRef.current?.querySelector<HTMLElement>('.bs4-dropdown-menu');
    if (!dropdownMenu) return;

    // Focus landed on a real element outside the menu — the element was removed but the
    // user had already navigated away. Restoring would override their deliberate action.
    const active = document.activeElement;
    if (active && active !== document.body && !dropdownMenu.contains(active)) return;

    const focusableItems = Array.from(
      dropdownMenu.querySelectorAll<HTMLElement>(FOCUSABLE_ITEMS_SELECTOR),
    );

    // Clamp to the last available item if the list shrank after the update.
    const targetItem = focusableItems[Math.min(savedIndex, focusableItems.length - 1)];
    targetItem?.focus();
  });
};

export default useDropdownFocusRestore;
